import type { Item } from "gamedata/GameData";
import { gameName } from "./common";
import { GameVer, lstring, Realm, tt } from "./Lang";
import { pg } from "~/pg.server";
import { and, eq } from "drizzle-orm";
import { loadMarkdown } from "./Markdown.server";
import { OurLoaderArgs } from "./Loaders";

export type ItemProps = {
  tooltip?: boolean;
  gver?: GameVer;
  item: Pick<
    Item,
    | "id"
    | "icon"
    | "itemName"
    | "crystalType"
    | "type"
    | "slotBitType"
    | "armorType"
    | "weaponType"
    | "physicalDamage"
    | "magicalDamage"
    | "attackSpeed"
    | "shieldDefense"
    | "shieldDefenseRate"
    | "avoidModify"
    | "physicalDefense"
    | "magicalDefense"
    | "soulshotCount"
    | "spiritshotCount"
    | "weight"
    | "defaultPrice"
    | "crystalCount"
    | "mpBonus"
    | "durability"
  > & { name: lstring; addName: lstring; desc: string };
};

export function getDescription(props: {
  item: ItemProps["item"];
  sources: (string | 0)[];
  realm: Realm;
}) {
  const { item, realm, sources } = props;
  const t = tt.bind(null, realm.lang);
  const fullname = {
    en: `${item.name.en} ${item.addName.en}`.trim(),
    ru: `${item.name.ru} ${item.addName.ru}`.trim(),
  };
  const game = gameName(realm.game);
  switch (item.type) {
    case "armor": {
      const armorNameEn = fullname.en;
      const armorNameRu = fullname.ru;
      const armorPDef = item.physicalDefense;
      const shieldPDef = item.shieldDefense;
      const armorSlot = t(getSlotBitType(item.slotBitType));
      const armorType = t(getArmorType(item.armorType));
      const armorGrade = t(getGrade(item.crystalType));
      const armorPrice = item.defaultPrice;
      const armorCrystalCount = item.crystalCount;
      const armorDesc = item.desc
        ? ` ${item.desc.replace(/\\\\n /g, " ")}`
        : "";

      return t({
        en: `${armorNameEn} is an item of type ${armorSlot} ${armorType}. ${armorGrade}. Base price is ${armorPrice} Adena.${armorDesc} ${game}`.trim(),
        ru: `Предмет ${armorNameRu} (${armorNameEn}), ${
          item.slotBitType !== "lhand"
            ? `${
                item.armorType !== "none" ? `тип "${armorType}",` : ""
              } физ. защита ${armorPDef}.`
            : `тип "${armorSlot}", защита щитом ${shieldPDef}.`
        } ${
          armorCrystalCount
            ? `При кристаллизации можно получить ${armorCrystalCount} Кристаллов ${armorGrade}. `
            : ""
        }Имеет базовую стоимость ${armorPrice} Аден. ${getSources(
          sources as string[]
        )}${armorDesc} ${game}`.trim(),
      });
    }

    case "weapon":
      const weaponNameEn = fullname.en;
      const weaponNameRu = fullname.ru;
      const weaponPAtk = item.physicalDamage;
      const weaponMAtk = item.magicalDamage;
      const weaponSlot = t(getSlotBitType(item.slotBitType));
      const weaponType = t(getWeaponType(item.weaponType));
      const weaponGrade = t(getGrade(item.crystalType));
      const weaponPrice = item.defaultPrice;
      const weaponCrystalCount = item.crystalCount;
      const weaponDesc = item.desc
        ? ` ${item.desc.replace(/\\\\n /g, " ")}`
        : "";

      return t({
        en: `${weaponNameEn} is an item of type ${weaponSlot} ${weaponType}. ${weaponGrade}. Base price is ${weaponPrice} Adena.${weaponDesc} ${game}`.trim(),
        ru: `Предмет ${weaponNameRu} (${weaponNameEn}), тип "${weaponType}", физ. атака ${weaponPAtk}, маг. атака ${weaponMAtk}. ${
          weaponCrystalCount
            ? `При кристаллизации можно получить ${weaponCrystalCount} Кристаллов ${weaponGrade}. `
            : ""
        }Имеет базовую стоимость ${weaponPrice} Аден. ${getSources(
          sources as string[]
        )}${weaponDesc} ${game}`.trim(),
      });

    case "accessary":
      const accessaryNameEn = fullname.en;
      const accessaryNameRu = fullname.ru;
      const accessarySlot = t(getSlotBitType(item.slotBitType));
      const accessaryMDef = item.magicalDefense;
      const accessaryCrystalCount = item.crystalCount;
      const accessaryGrade = t(getGrade(item.crystalType));
      const accessaryPrice = item.defaultPrice;
      const accessaryDesc = item.desc
        ? ` ${item.desc.replace(/\\\\n /g, " ")}`
        : "";

      return t({
        en: `${item.name.en} is an item of type ${accessarySlot}. ${accessaryGrade}. Base price is ${accessaryPrice} Adena.${accessaryDesc} ${game}`.trim(),
        ru: `Предмет ${accessaryNameRu} (${accessaryNameEn}), тип "${accessarySlot}", маг. защита ${accessaryMDef}. ${
          accessaryCrystalCount
            ? `При кристаллизации можно получить ${accessaryCrystalCount} Кристаллов ${accessaryGrade}. `
            : ""
        }Имеет базовую стоимость ${accessaryPrice} Аден. ${getSources(
          sources as string[]
        )}${accessaryDesc} ${game}`.trim(),
      });

    case "questitem":
      const qItemNameEn = fullname.en;
      const qItemNameRu = fullname.ru;
      const qItemDesc = item.desc
        ? ` ${item.desc.replace(/\\\\n /g, " ")}`
        : "";

      return t({
        en: `Quest item ${qItemNameEn}.${qItemDesc} ${game}`.trim(),
        ru: `Квестовый предмет ${qItemNameRu} (${qItemNameEn}).${qItemDesc} ${game}`.trim(),
      });

    default:
      const etcItemEn = fullname.en;
      const etcItemRu = fullname.ru;
      const etcItemDesc = item.desc
        ? ` ${item.desc.replace(/\\\\n /g, " ")}`
        : "";

      return t({
        en: `${etcItemEn}.${etcItemDesc} ${game}.`.trim(),
        ru: `${etcItemRu} (${etcItemEn}).${etcItemDesc} ${game}.`.trim(),
      });
  }
}

export function getType(props: { item: ItemProps["item"]; realm: Realm }) {
  const { item, realm } = props;
  const t = tt.bind(null, realm.lang);

  switch (item.type) {
    case "armor": {
      let buf = t(getSlotBitType(item.slotBitType));
      if (item.armorType && item.armorType !== "none") {
        buf += " / " + t(getArmorType(item.armorType));
      }
      return buf;
    }
    case "weapon":
      return (
        t(getWeaponType(item.weaponType)) +
        " / " +
        t(getSlotBitType(item.slotBitType))
      );
    case "accessary":
      return t(getSlotBitType(item.slotBitType));
  }
  return item.type;
}

function getArmorType(armorType: string | null) {
  switch (armorType) {
    case "heavy":
      return { en: "Heavy", ru: "Тяжелая броня" };
    case "light":
      return { en: "Light", ru: "Легкая броня" };
    case "magic":
      return { en: "Robe", ru: "Мантия" };
    default:
      return { en: armorType!, ru: armorType! };
  }
}

function getWeaponType(weaponType: string | null) {
  switch (weaponType) {
    case "blunt":
      return { en: "Blunt", ru: "Тупое" };
    case "bow":
      return { en: "Bow", ru: "Лук" };
    case "dagger":
      return { en: "Dagger", ru: "Кинжал" };
    case "etc":
      return { en: "Etc", ru: "Другое" };
    case "pole":
      return { en: "Pole", ru: "Древковое" };
    case "sword":
      return { en: "Sword", ru: "Меч" };
    default:
      return { en: weaponType!, ru: weaponType! };
  }
}

function getSlotBitType(slotBitType: string | null) {
  switch (slotBitType) {
    case "lrhand":
      return { en: "Two Handed", ru: "Двуручное" };
    case "rhand":
      return { en: "One Handed", ru: "Одноручное" };
    case "lhand":
      return { en: "Shield", ru: "Щит" };
    case "chest":
      return { en: "Upper Body", ru: "Верх" };
    case "feet":
      return { en: "Boots", ru: "Ботинки" };
    case "head":
      return { en: "Headgear", ru: "Головной убор" };
    case "gloves":
      return { en: "Gloves", ru: "Перчатки" };
    case "legs":
      return { en: "Lower Body", ru: "Низ" };

    case "neck":
      return { en: "Necklace", ru: "Ожерелье" };
    case "rfinger,lfinger":
      return { en: "Ring", ru: "Кольцо" };
    case "rear,lear":
      return { en: "Earing", ru: "Серьга" };
    default:
      return { en: slotBitType!, ru: slotBitType! };
  }
}

function getGrade(crystalType: string | null) {
  return {
    en:
      crystalType === "none"
        ? "No Grade"
        : `${crystalType?.toUpperCase()}-Grade`,
    ru:
      crystalType === "none"
        ? "Без ранга"
        : `${crystalType?.toUpperCase()}-Ранга`,
  };
}

function getSources(sources: string[]) {
  const arr = sources.map((source, i) => {
    switch (source) {
      case "craft":
        return "скрафтить";
      case "sell":
        return "купить";
      case "drop":
        return "выбить";
      case "spoil":
        return "заспойлить";
      case "multisell":
        return "обменять";
      case "reward":
        return "получить за квест (задание)";
      default:
        return source.toString();
    }
  });

  const firsts = arr.slice(0, arr.length - 1);
  const last = arr[arr.length - 1];

  return sources.length
    ? `Можно получить следующим способом: ${firsts.join(", ")} или ${last}.`
    : "";
}
export function itemName(item: { name: string; addName: string }) {
  return (
    <>
      {item.name}
      {item.addName && (
        <span style={{ color: "#fef27e" }}> [{item.addName}]</span>
      )}
    </>
  );
}

export async function itemMdPage(cx: OurLoaderArgs, itemName: string) {
  const page = await pg.db
    .select({
      contentEn: pg.mdRevisions.contentEn,
      contentRu: pg.mdRevisions.contentRu,
    })
    .from(pg.mdPages)
    .where(
      and(eq(pg.mdPages.itemName, itemName), eq(pg.mdPages.isDeleted, false))
    )
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.revisionId, pg.mdRevisions.id))
    .then((res) => res.at(0));

  return {
    content:
      page?.contentEn && cx.realm.lang === "en"
        ? await loadMarkdown(
            page?.contentEn,
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          )
        : page?.contentRu &&
          (await loadMarkdown(
            page?.contentRu,
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          )),
  };
}
